'use client';

import { useEffect } from 'react';
import Header from '@/components/Header';
import GrievanceOfficerContact from '@/components/GrievanceOfficerContact';
import ContentWithShortcodes, {
  hasGrievanceOfficerShortcode,
} from '@/components/ContentWithShortcodes';
import { PrivacyPolicyData } from '@/services/privacyService';

interface CommunitySitesClientProps {
  communitySitesData: PrivacyPolicyData;
}

export default function CommunitySitesClient({
  communitySitesData,
}: CommunitySitesClientProps) {
  useEffect(() => {
    const baseSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: communitySitesData.title,
      description:
        communitySitesData.seoDescription ||
        communitySitesData.metaDescription,
      url: `${
        process.env.NEXT_PUBLIC_SITE_URL || 'https://marathivadhuvarsuchak.com'
      }/community-sites`,
      datePublished: communitySitesData.createdAt,
      dateModified: communitySitesData.updatedAt,
      publisher: {
        '@type': 'Organization',
        name: 'Marathi Vadhu Var Suchak',
        url:
          process.env.NEXT_PUBLIC_SITE_URL ||
          'https://marathivadhuvarsuchak.com',
      },
    };

    const schemaPayload =
      communitySitesData.faqs && communitySitesData.faqs.length > 0
        ? [
            baseSchema,
            {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: communitySitesData.faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            },
          ]
        : baseSchema;

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schemaPayload);
    document.head.appendChild(schemaScript);

    return () => {
      const existingSchemas = document.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      existingSchemas.forEach((schema) => {
        if (schema.textContent?.includes(communitySitesData.title)) {
          schema.remove();
        }
      });
    };
  }, [communitySitesData]);

  return (
    <div className="bg-white">
      <Header variant="fixed" />

      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <article>
                <ContentWithShortcodes html={communitySitesData.content} />
              </article>

              {communitySitesData.faqs && communitySitesData.faqs.length > 0 && (
                <div className="mt-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-4">
                    {communitySitesData.faqs.map((faq) => (
                      <div
                        key={faq.id}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200">
                          <h3 className="text-lg font-semibold text-gray-900 pr-4">
                            {faq.question}
                          </h3>
                        </div>
                        <div className="px-6 py-4 bg-white border-t border-gray-200">
                          <div
                            className="content-prose-sm"
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!hasGrievanceOfficerShortcode(communitySitesData.content) && (
                <GrievanceOfficerContact />
              )}
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-200 info-box">
                <p className="text-blue-800 text-sm leading-relaxed">
                  This page lists official community-related sites and support
                  information published by our platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
